import { DateTime } from "luxon"
import { makeStyles } from "tss-react/mui"
import { ChevronRight } from "@mui/icons-material"
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material"
import { getCustomerGroupSalesByAccounts, getCustomerSalesByAccounts } from "../../accountingApi"
import { CustomerGroupAccordionDetails } from "./CustomerGroupAccordionDetails"
import { CustomerAccordionDetails } from "./CustomerAccordionDetails"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { AccountingDebtor } from "../../accountingTypes"
import { DateType } from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"

const useStyles = makeStyles()((theme) => ({
    accordionSummary: {
        flexDirection: "row-reverse",
        borderRadius: "0.75rem",
        backgroundColor: "#E2E8F0",
        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
            transform: "rotate(90deg)",
        },
        "&.Mui-expanded": {
            borderRadius: "0.75rem 0.75rem 0 0",
        },
        "& .MuiAccordionSummary-content": {
            marginLeft: theme.spacing(1),
        },
    },
    accordion: {
        borderRadius: "0.75rem !important",
        "&:before": {
            display: "none",
        },
        marginBottom: "2rem",
    },
}))

type CustomerAccordionProps = Readonly<{
    debtor: AccountingDebtor
    expanded: boolean
    startDate: DateTime
    endDate: DateTime
    shopIds: string | undefined
    dateType: DateType
    handleChange: any
}>

export function CustomerAccordion({
    debtor,
    expanded,
    handleChange,
    startDate,
    endDate,
    shopIds,
    dateType,
}: CustomerAccordionProps) {
    const { classes } = useStyles()

    const { merchantId } = useMerchant()

    const salesByAccounts = useApiQuery(getCustomerSalesByAccounts, {
        queryName: "customer-sales-by-account",
        dependencies: {
            merchantId: merchantId,
            customerId: debtor.id,
            shopIds,
            dateType,
            startDate: startDate.toISODate(),
            endDate: endDate.toISODate(),
        },
        enabled: expanded && debtor.type === "customer",
    })

    const groupSalesByAccounts = useApiQuery(getCustomerGroupSalesByAccounts, {
        queryName: "customer-group-sales-by-account",
        dependencies: {
            merchantId: merchantId,
            customerId: debtor.type === "customerGroup" ? debtor.customerId : 0,
            customerGroupId: debtor.id,
            shopIds,
            dateType,
            startDate: startDate.toISODate(),
            endDate: endDate.toISODate(),
        },
        enabled: expanded && debtor.type === "customerGroup",
    })

    return (
        <Accordion disableGutters expanded={expanded} onChange={handleChange(debtor)} className={classes.accordion}>
            <AccordionSummary
                expandIcon={<ChevronRight />}
                aria-controls="panel1bh-content"
                id="panel1bh-header"
                className={classes.accordionSummary}
            >
                <Typography variant="body2">{debtor.name}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {debtor.type === "customer" && (
                    <CustomerAccordionDetails key={debtor.id} salesByAccounts={salesByAccounts} />
                )}

                {debtor.type === "customerGroup" && (
                    <CustomerGroupAccordionDetails key={debtor.id} groupSalesByAccounts={groupSalesByAccounts} />
                )}
            </AccordionDetails>
        </Accordion>
    )
}
